import { Spin } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../../context/UserProvider';
import { Link } from 'react-router-dom';
import { BackIcon, LoginIcon, NotFoundIcon, SearchIcon, TruckIcon } from '../../lib/LandingIcons';
import { API } from '../../api';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import dayjs from 'dayjs';

const Tracking = () => {
  const [code, setCode] = useState('');
  const [lang, setLang] = useState<'en' | 'mn'>('mn');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [tracking, setTracking] = useState<any>(null);
  const [searchedCode, setSearchedCode] = useState('');
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  useEffect(() => {
    if (location.search) {
      const search = queryString.parse(location.search);
      const nextLang = search.lang === 'en' ? 'en' : 'mn';
      setLang(nextLang);
      if (search.code) {
        setCode(search.code as string);
        searchTracking(search.code as string, nextLang);
      }
    }
  }, [location.search, navigate]);
  const langValues = {
    en: {
      country: 'Mongolia',
      heading: 'Track your parcel',
      example: 'Example',
      trackNumber: 'Tracking number',
      updated: 'Updated',
      latest: 'Latest',
      notFoundTitle: 'Parcel not found',
      notFoundText:
        'No parcel is registered with number “%s”. Please check the number and try again.',
      back: 'Back to home',
      login: 'Log in',
      searchPlaceholder: 'input search barcode',
      submitButton: 'Submit',
      trackHistory: 'Track History',
      trackNotFound: 'Track not found',
      RECEIVED: 'Accepted at %s',
      SENT_TO_TML: 'Departed %s',
      RECEIVED_TO_TML: 'Arrived at %s',
      SENT_FROM_TML: 'Departed %s',
      RECEIVED_TO_LAST_BRANCH: 'Arrived at %s',
      DELIVERY_STARTED: 'Out for Delivery',
      DELIVERY_STARTED_PREFIX: 'Departed %s',
      DELIVERY_COMPLETED: 'Delivered',
      NOT_DELIVERED: 'Delivery failed - %r',
      RETURN_DELIVERY_STARTED: 'Захиалагч буцаасан',
      RETURN_DELIVERY_COMPLETED: 'Буцаалт илгээсэн',
      MNPOST_RETURN: 'Overseas undelivered return delivery',
      SENT_TO_BRANCH: 'Departed %s',
      //reason
      NO_RECIPENT: 'Consumer not at home',
      WRONG_ADDRESS: 'Address error',
      BROKEN: 'Consumers demand reassignment',
      PHONE_UNREACHABLE: 'unable to contact consumer',
      WANTED_RETURN: 'Consumers demand reassignment',
      CHANGED_ADDRESS: 'Consumers demand reassignment',
    },
    mn: {
      country: 'Монгол улс',
      heading: 'Илгээмжийн байршил шалгах',
      example: 'Жишээ',
      trackNumber: 'Илгээмжийн дугаар',
      updated: 'Шинэчлэгдсэн',
      latest: 'Сүүлийн төлөв',
      notFoundTitle: 'Илгээмж олдсонгүй',
      notFoundText: '“%s” дугаартай илгээмж бүртгэлгүй байна. Дугаараа шалгаад дахин оролдоно уу.',
      back: 'Нүүр хуудас руу буцах',
      login: 'Нэвтрэх',
      searchPlaceholder: 'Илгээмжийн дугаар',
      submitButton: 'Шалгах',
      trackHistory: 'Захиалгын түүх',
      trackNotFound: 'Илгээмж бүртгэлгүй байна',
      RECEIVED: '%s-т ирлээ',
      SENT_TO_TML: '%s -с гарлаа',
      RECEIVED_TO_TML: '%s-т ирлээ',
      SENT_FROM_TML: '%s -с гарлаа ',
      RECEIVED_TO_LAST_BRANCH: '%s-т ирлээ',
      DELIVERY_STARTED: 'Хүргэлт эхэлсэн',
      DELIVERY_STARTED_PREFIX: '%s -с гарлаа',
      DELIVERY_COMPLETED: 'Хүргэгдсэн',
      NOT_DELIVERED: 'Хүргэлт амжилтгүй - %r',
      RETURN_DELIVERY_STARTED: 'Захиалагч буцаасан',
      RETURN_DELIVERY_COMPLETED: 'Буцаалт илгээсэн',
      SENT_TO_BRANCH: '%s -ээс гарлаа',
      MNPOST_RETURN: 'Буцаасан',
      //reason
      NO_RECIPENT: 'Гэртээ байхгүй байна',
      WRONG_ADDRESS: 'Хаяг буруу',
      BROKEN: 'Буцаалт хүссэн',
      PHONE_UNREACHABLE: 'Хүлээн авагчтай холбогдох боломжгүй',
      WANTED_RETURN: 'Буцаалт хүссэн',
      CHANGED_ADDRESS: 'Хаяг солисон',
    },
  };
  const goPage = (lang?: string, code?: string) => {
    navigate(`/track?${queryString.stringify({ lang, code })}`);
  };
  const searchTracking = async (code: string, lang?: string) => {
    try {
      setLoading(true);
      setSearchedCode(code);
      const response = await API.get({})(`/tracking?nums=${code}&lang=${lang}`);
      const data = response.result?.list ?? [];
      setTracking(
        data.find((item: any) => item.trackNumber === code || item.trackingNumber === code) ?? null
      );
    } catch (error) {
      setTracking(null);
    } finally {
      setLoading(false);
    }
  };

  // newest event first
  const history = useMemo(() => {
    const list = [...(tracking?.history ?? [])];
    return list.sort((a: any, b: any) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
  }, [tracking]);
  const latest = history[0];
  const t = langValues[lang];
  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const value = code.trim().toUpperCase();
    if (value) goPage(lang, value);
  };

  return (
    <div className="tk-page">
      <header className="tk-top">
        <Link className="tk-brand" to="/">
          <img className="tk-mp" src="/images/mongolpost-logo.png" alt="Монгол Шуудан" />
          <span className="tk-sep" />
          <img className="tk-md" src="/images/logo.png" alt="MyDelivery" />
        </Link>
        <div className="tk-actions">
          <Link className="tk-login" to={user ? '/dashboard' : '/auth/login'}>
            <LoginIcon />
            {t.login}
          </Link>
          <div className="tk-lang" role="group" aria-label="Language">
            {(['mn', 'en'] as const).map((l) => (
              <button
                key={l}
                type="button"
                className={lang === l ? 'on' : ''}
                onClick={() => goPage(l, searchedCode || code || undefined)}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="tk-hero">
        <p>{t.heading}</p>
      </div>

      <main className="tk-main">
        <section className="tk-card">
          <form className="tk-field" onSubmit={submit}>
            <SearchIcon className="tk-si" />
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t.searchPlaceholder}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
            />
            <button type="submit" className="tk-go" disabled={loading}>
              {t.submitButton}
            </button>
          </form>
          <p className="tk-hint">{t.example}: MD104857362MN</p>
        </section>

        {loading && (
          <section className="tk-card tk-loading">
            <Spin />
          </section>
        )}

        {!loading && tracking && (
          <section className="tk-card">
            <div className="tk-sum">
              <div>
                <small>{t.trackNumber}</small>
                <div className="tk-no">
                  {tracking.trackNumber || tracking.trackingNumber || searchedCode}
                </div>
              </div>
              {latest && (
                <div className="tk-sum-r">
                  <span className="tk-badge">
                    <TruckIcon />
                    {latest.description}
                  </span>
                  <div className="tk-upd">
                    {t.updated}: {dayjs(latest.date).format('YYYY.MM.DD HH:mm')}
                  </div>
                </div>
              )}
            </div>
            <ul className="tk-tl">
              {history.map((item: any, index: number) => (
                <li key={index} className={index === 0 ? 'now' : ''}>
                  <div className="tk-dot" />
                  <div className="tk-what">
                    <div className="tk-when">
                      {dayjs(item.date).format('YYYY-MM-DD HH:mm')}
                      {index === 0 && <em>{t.latest}</em>}
                    </div>
                    {item.description}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!loading && !tracking && searchedCode && (
          <section className="tk-card">
            <div className="tk-empty">
              <div className="tk-empty-ic">
                <NotFoundIcon />
              </div>
              <b>{t.notFoundTitle}</b>
              <p>{t.notFoundText.replace('%s', searchedCode)}</p>
            </div>
          </section>
        )}

        <Link className="tk-back" to="/">
          <BackIcon />
          {t.back}
        </Link>
      </main>

      <p className="tk-powered">Powered by MongolPost 2026</p>
    </div>
  );
};
export default Tracking;
